import React, { useCallback, useEffect, useRef, useState } from 'react';

import { ChatMessage, IfHasPermission, ReadOnlyProjectNotice } from '@ui-library';

import type { Message } from '../../types/chat';
import type { User } from '../../contexts/AuthContext';
import type { CABTag } from '../../types/cabTags';
import { Permissions } from '../../constants';
import { CABChatInput } from './CABChatInput';

interface ICABChatPanelProps {
  projectId: string;
  messages: Message[];
  onSendMessage: (content: string, tags: CABTag[], modelPreference?: string) => Promise<void>;
  isSending: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  permissions?: string[];
  currentUser?: User | null;
  hasMoreMessages?: boolean;
  isLoadingMore?: boolean;
  onLoadMore?: () => Promise<void>;
}

export const CABChatPanel: React.FC<ICABChatPanelProps> = ({
  projectId,
  messages,
  onSendMessage,
  isSending,
  disabled = false,
  readOnly = false,
  permissions,
  currentUser,
  hasMoreMessages = false,
  isLoadingMore = false,
  onLoadMore,
}) => {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const [hasUserScrolledUp, setHasUserScrolledUp] = useState(false);
  const scrollTimeoutRef = useRef<number | undefined>();
  const isLoadingMoreRef = useRef(false);

  // Keep ref in sync with prop
  useEffect(() => {
    isLoadingMoreRef.current = isLoadingMore;
  }, [isLoadingMore]);

  // Auto-scroll to bottom only when user hasn't manually scrolled up
  useEffect(() => {
    if (!hasUserScrolledUp && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, hasUserScrolledUp]);

  // Load more messages handler
  const loadMore = useCallback(async (container: HTMLDivElement) => {
    if (isLoadingMoreRef.current || !hasMoreMessages || !onLoadMore) return;

    const prevScrollHeight = container.scrollHeight;
    const prevScrollTop = container.scrollTop;

    try {
      await onLoadMore();
    } finally {
      // Restore scroll position after prepending older messages
      requestAnimationFrame(() => {
        const newScrollHeight = container.scrollHeight;
        container.scrollTop = newScrollHeight - prevScrollHeight + prevScrollTop;
      });
    }
  }, [hasMoreMessages, onLoadMore]);

  // Handle scroll to detect if user is at bottom and trigger pagination
  const handleScroll = useCallback(() => {
    const container = chatContainerRef.current;
    if (!container) return;

    const { scrollTop, scrollHeight, clientHeight } = container;
    const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
    const threshold = 100;

    const atBottom = distanceFromBottom < threshold;

    // If user scrolled up manually (away from bottom), set the flag
    if (!atBottom && !hasUserScrolledUp) {
      setHasUserScrolledUp(true);
    }

    // If user scrolled back to bottom manually, re-enable auto-scroll
    if (atBottom && hasUserScrolledUp) {
      setHasUserScrolledUp(false);
    }

    // Debounce the scroll checks for load more
    if (scrollTimeoutRef.current) {
      window.clearTimeout(scrollTimeoutRef.current);
    }

    const SCROLL_THRESHOLD = 100; // pixels from top to trigger load more

    scrollTimeoutRef.current = window.setTimeout(() => {
      if (scrollTop < SCROLL_THRESHOLD && !isLoadingMoreRef.current) {
        if (hasMoreMessages && !isLoadingMore) {
          loadMore(container);
        }
      }
    }, 80);
  }, [hasUserScrolledUp, hasMoreMessages, isLoadingMore, loadMore]);

  // Cleanup scroll timeout on unmount
  useEffect(() => {
    return () => {
      if (scrollTimeoutRef.current) {
        window.clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, []);

  return (
    <div className="flex-1 flex flex-col min-h-0" role="tabpanel" id="panel-chat">
      <div
        ref={chatContainerRef}
        className="flex-1 overflow-y-auto px-bg-v2-lg py-bg-v2-lg modern-scrollbar-spaced"
        onScroll={handleScroll}
      >
        <div className="flex flex-col gap-bg-v2-lg">
          {/* Loading indicator for older messages */}
          {isLoadingMore && (
            <div className='flex justify-center py-2'>
              <div className='flex items-center gap-2 text-sm text-text-tertiary'>
                <div className='animate-spin rounded-full h-4 w-4 border-b-2 border-border-brand'></div>
                <span>Loading more messages...</span>
              </div>
            </div>
          )}
          {messages.map((msg) => (
            <ChatMessage
              key={msg.id}
              message={msg}
              project={null}
              currentUser={currentUser || null}
              isFullWidthMessages={true}
            />
          ))}
          <div ref={messagesEndRef} />
        </div>
      </div>
      {/* Same gate as the Build and Design composers: asking needs Ask mode, editing
          needs update. Viewers can ask (edit requests are rejected server-side with a
          friendly message, so only the placeholder changes); guests hold neither and
          see the read-only notice instead of an input. */}
      <div className="px-bg-v2-lg pb-bg-v2-lg">
        <IfHasPermission
          permissions={permissions}
          requires={[Permissions.AskModePermission, Permissions.UpdateProjectPermission]}
          fallback={<ReadOnlyProjectNotice />}
        >
          <CABChatInput
            projectId={projectId}
            onSend={onSendMessage}
            isSending={isSending}
            disabled={disabled}
            placeholder={
              readOnly
                ? 'Ask anything about your CAB...'
                : 'Ask anything about your CAB, or request changes...'
            }
          />
        </IfHasPermission>
      </div>
    </div>
  );
};
